import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BgColorsOutlined,
  BoldOutlined,
  FontColorsOutlined,
  ItalicOutlined,
  UnderlineOutlined,
} from "@ant-design/icons";
import { Button, InputNumber, Segmented, Select, Space, Tooltip, Typography } from "../../../../../shared/frontend/ui";
import React from "react";
import { spacing } from "../app/design-system";
import type { LocalizedText } from "../app/i18n/i18n";
import { useI18n } from "../app/i18n/useI18n";
import { useDecimalSeparator } from "../app/units/useDecimalSeparator";
import { HexColorPicker } from "./HexColorPicker";

export type TextFormatMarkKey = "bold" | "italic" | "underline";

export type TextFormatMarks = Partial<Record<TextFormatMarkKey, boolean | null>>;

/**
 * Ausrichtungswerte der Leiste. Wertgleich mit `TEXT_ALIGNMENT` des Erscheinungsbilds und
 * `SYSTEM_DOCUMENT_TEXT_ALIGNMENT` der Systemtexte — beide geben ihre eigenen Konstanten durch.
 */
const TEXT_FORMAT_ALIGNMENT = { left: 0, center: 1, right: 2 } as const;

const MARKS: Array<[TextFormatMarkKey, React.ComponentType, LocalizedText]> = [
  ["bold", BoldOutlined, "Bold"],
  ["italic", ItalicOutlined, "Italic"],
  ["underline", UnderlineOutlined, "Underline"],
];

const SWATCH_SIZE = 32;

type Props = {
  /** Worauf die Leiste gerade wirkt: Baustein, Textfeld oder Datenrolle. */
  label?: React.ReactNode;
  /** Aktive Auszeichnungen. Ohne Angabe wird kein gedrückter Zustand gezeigt. */
  marks?: TextFormatMarks;
  onToggleMark: (key: TextFormatMarkKey, active: boolean) => void;
  marksDisabled?: boolean;
  /** `undefined` überlässt dem Farbfeld seinen eigenen Stand — für Auswahlen ohne festen Wert. */
  textColor?: string | null;
  backgroundColor?: string | null;
  onTextColorChange: (color: string | null) => void;
  onBackgroundColorChange: (color: string | null) => void;
  /** Tabellen dürfen neben dem Standard ausdrücklich ohne Zellfüllung dargestellt werden. */
  backgroundCanBeTransparent?: boolean;
  colorsDisabled?: boolean;
  /**
   * Wechselt mit dem Ziel der Leiste. Die Farbfelder werden damit neu aufgebaut, sonst zeigten sie
   * beim Sprung auf ein anderes Ziel noch dessen vorige Farbe.
   */
  colorResetKey?: string;
  alignment?: number | null;
  onAlignmentChange?: (alignment: number) => void;
  alignmentDisabled?: boolean;
  /** Wählbare Schriften für Schriftart-Auswahl. */
  fontOptions?: Array<{ value: string; label: string }>;
  fontFamily?: string | null;
  onFontFamilyChange?: (font: string | null) => void;
  /** Schriftgrad in Punkt. Ohne Rückruf bietet die Leiste ihn nicht an. */
  fontSizePt?: number | null;
  onFontSizeChange?: (value: number | null) => void;
  /** Zusätzliche Bedienelemente am Ende der Leiste. */
  extra?: React.ReactNode;
};

/**
 * Die Zeichenformatierung dessen, was gerade in einer Dokumentvorschau ausgewählt ist:
 * Auszeichnung, Farbe, Ausrichtung und wahlweise der Schriftgrad.
 *
 * **Einzige** Formatleiste der Dokumenteditoren. Sie lag zuvor dreimal nebeneinander — im
 * Systemtext-Editor für Textfelder, dort ein zweites Mal für Datenrollen und im Erscheinungsbild
 * für Bausteine —, mit unterschiedlicher Optik und verschieden gebautem Aktiv-Zustand.
 *
 * Worauf die Leiste wirkt, weiß nur der Aufrufer: Bei einem bearbeitbaren Absatz setzt er ein
 * Editorkommando, bei einer Auswahl ohne Schreibmarke einen gespeicherten Stil. Deshalb liefert die
 * Leiste den Klick und den angezeigten Zustand, nie die Wirkung.
 */
export function TextFormatToolbar({
  label,
  marks,
  onToggleMark,
  marksDisabled = false,
  textColor,
  backgroundColor,
  onTextColorChange,
  onBackgroundColorChange,
  backgroundCanBeTransparent = false,
  colorsDisabled = false,
  colorResetKey = "",
  alignment,
  onAlignmentChange,
  alignmentDisabled = false,
  fontOptions,
  fontFamily,
  onFontFamilyChange,
  fontSizePt,
  onFontSizeChange,
  extra,
}: Props) {
  const { text } = useI18n();
  const decimalSeparator = useDecimalSeparator();

  return (
    <Space size={spacing.xs} wrap>
      {label != null ? <Typography.Text type="secondary">{label}</Typography.Text> : null}

      {onFontFamilyChange ? (
        <Tooltip title={text("Font family")}>
          <Select
            allowClear
            disabled={marksDisabled}
            placeholder={text("Standard")}
            value={fontFamily ?? undefined}
            onChange={(val) => onFontFamilyChange(val ?? null)}
            options={fontOptions}
            style={{ width: 140 }}
          />
        </Tooltip>
      ) : null}

      {onFontSizeChange ? (
        <Tooltip title={text("Font size")}>
          <InputNumber
            min={5}
            max={72}
            step={0.5}
            decimalSeparator={decimalSeparator}
            addonAfter="pt"
            placeholder={text("Standard")}
            aria-label={text("Font size")}
            disabled={marksDisabled}
            value={fontSizePt ?? null}
            onChange={onFontSizeChange}
            style={{ width: 100 }}
          />
        </Tooltip>
      ) : null}

      <Button.Group>
        {MARKS.map(([key, Icon, title]) => {
          const active = marks?.[key] === true;
          return (
            <Tooltip key={key} title={text(title)}>
              <Button
                type={active ? "primary" : "default"}
                icon={<Icon />}
                aria-label={text(title)}
                aria-pressed={active}
                disabled={marksDisabled}
                // Sonst verlöre ein bearbeiteter Absatz beim Klick die Schreibmarke, und das
                // Editorkommando träfe keine Auswahl mehr.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onToggleMark(key, active)}
              />
            </Tooltip>
          );
        })}
      </Button.Group>

      <Tooltip title={text("Text color")}>
        <span>
          <HexColorPicker
            key={`text-color-${colorResetKey}`}
            triggerMode="swatch"
            commitMode="explicit"
            allowClear
            placeholder={text("Text color")}
            icon={<FontColorsOutlined />}
            style={{ width: SWATCH_SIZE, height: SWATCH_SIZE }}
            disabled={colorsDisabled}
            value={textColor}
            onChange={onTextColorChange}
            nonColorOptions={[{ value: null, label: text("Standard") }]}
          />
        </span>
      </Tooltip>

      <Tooltip title={text("Background color")}>
        <span>
          <HexColorPicker
            key={`background-color-${colorResetKey}`}
            triggerMode="swatch"
            commitMode="explicit"
            allowClear
            placeholder={text("Background color")}
            icon={<BgColorsOutlined />}
            style={{ width: SWATCH_SIZE, height: SWATCH_SIZE }}
            disabled={colorsDisabled}
            value={backgroundColor}
            onChange={onBackgroundColorChange}
            nonColorOptions={backgroundCanBeTransparent
              ? [
                  { value: null, label: text("Standard") },
                  { value: "transparent", label: text("No color") },
                ]
              : [{ value: null, label: text("Standard") }]}
          />
        </span>
      </Tooltip>

      {onAlignmentChange ? (
        <Segmented
          value={alignment ?? TEXT_FORMAT_ALIGNMENT.left}
          disabled={alignmentDisabled}
          onChange={(value) => onAlignmentChange(Number(value))}
          // Ausrichtung als Symbol: Das Bild sagt hier mehr als das Wort, und die Leiste bleibt
          // dadurch in einer Zeile.
          options={[
            { value: TEXT_FORMAT_ALIGNMENT.left, label: <AlignLeftOutlined />, title: text("Left") },
            { value: TEXT_FORMAT_ALIGNMENT.center, label: <AlignCenterOutlined />, title: text("Centered") },
            { value: TEXT_FORMAT_ALIGNMENT.right, label: <AlignRightOutlined />, title: text("Right") },
          ]}
        />
      ) : null}

      {extra}
    </Space>
  );
}
